/*
  Область выбора файлов вкладки «Файлы»: перетащить или выбрать, несколько сразу.

  Та же крупная область `.pickfile`, что у шагов «Выгрузки 1С»: человек открывает
  раздел, чтобы прислать файл, и видеть поле он должен первым делом. Ошибка
  приёма стоит здесь же, под областью, - рядом с действием, которое её вызвало.

  Итог приёма - тоже здесь (01.10.2026, проверка перед выкладкой): сервер
  отвечает, какие файлы принял, а какие нет и почему. Отказанный файл
  называется с причиной и подсказкой, а принятый повторно - словами «уже был
  прислан»: иначе человек ищет в таблице новую строку, которой не будет.
*/

import { useState } from 'react'

import { useAccess } from '../AccessContext.jsx'
import { Notice } from '../Common.jsx'

export const ACCEPT = '.xlsx,.xlsm,.csv,.docx,.pdf,.jpg,.jpeg,.png'

// Что сказал сервер о присланном последним нажатием: отказы с причиной и
// повторы. Ключ - порядковый номер в ответе: список только на чтение и
// заменяется целиком, а одно имя файла может прийти дважды.
function Outcome({ sent }) {
  if (!sent) return null
  const repeated = sent.accepted.filter((file) => file.duplicate)
  return (
    <>
      {sent.refused.map((file, index) => (
        <Notice key={index} kind="warn">
          <div className="notice__what filename">
            «{file.filename}» не принят: {file.message}
          </div>
          {file.remedy ? <div className="notice__do">{file.remedy}</div> : null}
        </Notice>
      ))}
      {repeated.length ? (
        <Notice kind="ok">
          {repeated.map((file) => (
            <div key={file.id} className="filename">
              «{file.filename}» - этот файл уже был прислан, в таблице он остаётся одной строкой.
            </div>
          ))}
        </Notice>
      ) : null}
    </>
  )
}

export default function FileDrop({ onFiles, busy, error, sent }) {
  const [over, setOver] = useState(false)
  // Брошенное во время приёма раньше пропадало молча (01.10.2026): человек
  // считал файл присланным. Теперь такие файлы называются.
  const [late, setLate] = useState([])
  // Перетаскивание идёт мимо замка: `fieldset disabled` гасит поле выбора, но не
  // обработчик на области (ревью 30.10.2026) - брошенный файл уходил и получал
  // отказ «Лицензия не оплачена». Плашка каркаса объясняет причину.
  const { readonly } = useAccess()

  const take = (list) => {
    const files = Array.from(list ?? [])
    if (!files.length || readonly) return
    if (busy) {
      setLate(files.map((file) => file.name))
      return
    }
    setLate([])
    onFiles(files)
  }

  // Белая карточка - как у шагов «Выгрузки 1С» (указание владельца 02.10.2026):
  // без неё та же область лежала на сером фоне страницы и выглядела другим
  // элементом.
  return (
    <section className="section card">
      <div
        className={`pickfile${over ? ' pickfile--over' : ''}`}
        onDragOver={(event) => {
          event.preventDefault()
          setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault()
          setOver(false)
          take(event.dataTransfer.files)
        }}
      >
        <label className="pickfile__control">
          <span className="pickfile__label">
            {busy ? 'Принимаем файлы...' : 'Перетащите файлы сюда или выберите'}
          </span>
          <span className="pickfile__note">
            таблицы Excel и CSV, документы Word и PDF, сканы и фото, можно несколько сразу · файл
            хранится месяц, а давший сведения счёту - пока счёт в портфеле
          </span>
          <input
            className="field__file pickfile__input"
            type="file"
            multiple
            accept={ACCEPT}
            disabled={busy}
            onChange={(event) => {
              take(event.target.files)
              event.target.value = ''
            }}
          />
        </label>
      </div>
      {late.length ? (
        <Notice kind="warn">
          <div className="notice__what filename">
            Не приняты: {late.map((name) => `«${name}»`).join(', ')} - в это время шёл приём
            предыдущих файлов.
          </div>
          <div className="notice__do">Дождитесь окончания приёма и пришлите их снова.</div>
        </Notice>
      ) : null}
      <Notice error={error} />
      <Outcome sent={sent} />
    </section>
  )
}
